import { useEffect, useState } from 'react';
import { FiEdit2, FiMail, FiPower, FiUserPlus } from 'react-icons/fi';
import { Header, Sidebar } from '../../components/layout';
import { SearchableSelect } from '../../components/ui';
import { useAuth } from '../../contexts';
import { api } from '../../services/api';
import styles from './GestaoUsuarios.module.css';

type Role = 'ADMIN' | 'GERENTE' | 'OPERADOR';
type Status = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

interface UsuarioItem {
    id: string;
    name: string;
    email: string;
    role: Role;
    status: Status;
    createdAt: string;
}

const ROLE_LABELS: Record<Role, string> = {
    ADMIN: 'Admin',
    GERENTE: 'Gerente',
    OPERADOR: 'Operador',
};

const STATUS_LABELS: Record<Status, string> = {
    ACTIVE: 'Ativo',
    INACTIVE: 'Inativo',
    SUSPENDED: 'Suspenso',
};

export function GestaoUsuarios() {
    const { user } = useAuth();
    const [usuarios, setUsuarios] = useState<UsuarioItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showConvite, setShowConvite] = useState(false);
    const [convite, setConvite] = useState({ name: '', email: '', role: 'OPERADOR' as Role });
    const [enviando, setEnviando] = useState(false);
    const [editandoRole, setEditandoRole] = useState<string | null>(null);
    const [confirmModal, setConfirmModal] = useState<{ id: string; role: Role; nome: string } | null>(null);

    const carregar = async () => {
        try {
            setIsLoading(true);
            const data = await api.getUsuariosGestao();
            setUsuarios(data);
        } catch (err) {
            console.error('Erro ao carregar usuários:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleConvidar = async () => {
        try {
            setEnviando(true);
            await api.convidarUsuario(convite);
            setShowConvite(false);
            setConvite({ name: '', email: '', role: 'OPERADOR' });
            await carregar();
        } finally {
            setEnviando(false);
        }
    };

    const handleToggleStatus = async (u: UsuarioItem) => {
        const novoStatus = u.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        await api.atualizarUsuarioStatus(u.id, novoStatus);
        await carregar();
    };

    const handleAlterarRole = async (id: string, role: Role) => {
        if (id === user?.id && role === 'OPERADOR') {
            const u = usuarios.find(u => u.id === id);
            setConfirmModal({ id, role, nome: u?.name || '' });
            setEditandoRole(null);
            return;
        }
        await api.atualizarUsuarioRole(id, role);
        setEditandoRole(null);
        await carregar();
    };

    const confirmarAlteracaoRole = async () => {
        if (!confirmModal) return;
        await api.atualizarUsuarioRole(confirmModal.id, confirmModal.role);
        setConfirmModal(null);
        await carregar();
    };

    useEffect(() => { carregar(); }, []);

    return (
        <div className={styles.layout}>
            <Sidebar />
            <main className={styles.main}>
                <Header title="Gestão de Usuários" />
                <div className={styles.content}>
                    <div className={styles.headerRow}>
                        <p className={styles.subtitle}>{usuarios.length} usuário(s) cadastrado(s)</p>
                        <button className={styles.btnConvidar} onClick={() => setShowConvite(true)}>
                            <FiUserPlus size={16} />
                            Convidar Usuário
                        </button>
                    </div>

                    {showConvite && (
                        <div className={styles.conviteCard}>
                            <h2 className={styles.conviteTitle}>Novo Convite</h2>
                            <div className={styles.conviteForm}>
                                <input
                                    className={styles.input}
                                    placeholder="Nome"
                                    value={convite.name}
                                    onChange={e => setConvite(p => ({ ...p, name: e.target.value }))}
                                />
                                <input
                                    className={styles.input}
                                    placeholder="E-mail"
                                    type="email"
                                    value={convite.email}
                                    onChange={e => setConvite(p => ({ ...p, email: e.target.value }))}
                                />
                                <SearchableSelect
                                    className={styles.select}
                                    value={convite.role}
                                    onChange={(v) => setConvite(p => ({ ...p, role: v as Role }))}
                                    options={[
                                        { value: 'OPERADOR', label: 'Operador' },
                                        { value: 'GERENTE', label: 'Gerente' },
                                        { value: 'ADMIN', label: 'Admin' },
                                    ]}
                                />
                            </div>
                            <div className={styles.conviteActions}>
                                <button className={styles.btnCancelar} onClick={() => setShowConvite(false)}>
                                    Cancelar
                                </button>
                                <button
                                    className={styles.btnEnviar}
                                    onClick={handleConvidar}
                                    disabled={enviando || !convite.name || !convite.email}
                                >
                                    <FiMail size={14} />
                                    {enviando ? 'Enviando...' : 'Enviar Convite'}
                                </button>
                            </div>
                        </div>
                    )}

                    {isLoading ? (
                        <div className={styles.loading}>Carregando...</div>
                    ) : (
                        <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Usuário</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Cadastro</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usuarios.map(u => (
                                        <tr key={u.id}>
                                            <td>
                                                <div className={styles.userCell}>
                                                    <div className={styles.avatar}>
                                                        {u.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                                    </div>
                                                    <div>
                                                        <div className={styles.userName}>{u.name}</div>
                                                        <div className={styles.userEmail}>{u.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {editandoRole === u.id ? (
                                                    <div
                                                        tabIndex={-1}
                                                        onBlur={(e) => {
                                                            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                                                setEditandoRole(null);
                                                            }
                                                        }}
                                                    >
                                                        <SearchableSelect
                                                            className={styles.selectInline}
                                                            value={u.role}
                                                            onChange={(v) => handleAlterarRole(u.id, v as Role)}
                                                            options={[
                                                                { value: 'OPERADOR', label: 'Operador' },
                                                                { value: 'GERENTE', label: 'Gerente' },
                                                                { value: 'ADMIN', label: 'Admin' },
                                                            ]}
                                                        />
                                                    </div>
                                                ) : (
                                                    <span className={`${styles.roleBadge} ${styles[`role${u.role}`]}`}>
                                                        {ROLE_LABELS[u.role]}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${styles[`status${u.status}`]}`}>
                                                    {STATUS_LABELS[u.status]}
                                                </span>
                                            </td>
                                            <td className={styles.dateCell}>
                                                {new Date(u.createdAt).toLocaleDateString('pt-BR')}
                                            </td>
                                            <td>
                                                <div className={styles.actions}>
                                                    <button
                                                        className={styles.btnAction}
                                                        title="Alterar role"
                                                        onClick={() => setEditandoRole(u.id)}
                                                    >
                                                        <FiEdit2 size={14} />
                                                    </button>
                                                    <button
                                                        className={`${styles.btnAction} ${u.status === 'ACTIVE' ? styles.btnDeactivate : styles.btnActivate}`}
                                                        title={u.status === 'ACTIVE' ? 'Desativar' : 'Ativar'}
                                                        onClick={() => handleToggleStatus(u)}
                                                    >
                                                        <FiPower size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {confirmModal && (
                    <div className={styles.modalOverlay}>
                        <div className={styles.modal}>
                            <h3 className={styles.modalTitle}>Atenção</h3>
                            <p className={styles.modalText}>
                                Você está alterando sua própria permissão para <strong>{ROLE_LABELS[confirmModal.role]}</strong>. Você perderá o acesso à Gestão de Usuários. Deseja continuar?
                            </p>
                            <div className={styles.modalActions}>
                                <button className={styles.btnCancelar} onClick={() => setConfirmModal(null)}>
                                    Cancelar
                                </button>
                                <button className={styles.btnPerigo} onClick={confirmarAlteracaoRole}>
                                    Sim, alterar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}